<template>
  <div class="w-full p-4">
    <!-- Title, count and filters travel together and stay on screen, as on
         My Expenses. -mt-4 cancels the page's own p-4, which otherwise
         leaves 16px of slack before the band catches. -->
    <div class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Request Triage</h2>
      </div>

      <p class="text-sm text-gray-500">
        {{ total }} request{{ total === 1 ? '' : 's' }} — click one to read its
        thread and reply, or Open to type it, estimate it, or give it a
        backlog item.
      </p>

      <!-- The number anybody wants on arrival: how much of this queue is
           waiting on us. A button rather than a label, because the next
           thing you want is to see only those. -->
      <div class="mt-2 flex items-center gap-2 flex-wrap text-sm">
        <button
          v-if="needsUsCount"
          class="px-2.5 py-1 rounded-md border font-semibold"
          :class="needsUsOnly
            ? 'bg-orange-600 border-orange-600 text-white'
            : 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100'"
          :title="needsUsOnly
            ? 'Show every request again'
            : 'Show only the ones waiting on us'"
          @click="needsUsOnly = !needsUsOnly"
        >
          {{ needsUsCount }} need{{ needsUsCount === 1 ? 's' : '' }} us
        </button>
        <!-- Said in green rather than left blank: an empty queue and a
             broken counter look identical if nothing is on screen. -->
        <span
          v-else
          class="px-2.5 py-1 rounded-md bg-green-50 border border-green-300 text-green-800"
        >
          Nothing waiting on us
        </span>

        <span v-if="awaitingReplyCount" class="text-orange-800">
          · {{ awaitingReplyCount }} awaiting a reply
        </span>
        <span v-if="needsUsOnly" class="text-gray-500">
          · showing {{ visibleRequests.length }} of {{ requests.length }}
        </span>
        <button
          v-if="expandedIds.length"
          class="text-gray-600 underline ml-1"
          @click="collapseAll"
        >
          Collapse all
        </button>
      </div>

      <div class="mt-3 flex items-center gap-2 flex-wrap">
        <select
          v-model="status"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          @change="load"
        >
          <option value="">All statuses</option>
          <option v-for="s in REQUEST_STATUSES" :key="s.value" :value="s.value">
            {{ s.label }}
          </option>
        </select>
        <select
          v-model="kind"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          @change="load"
        >
          <option value="">All types</option>
          <option v-for="k in REQUEST_KINDS" :key="k.value" :value="k.value">
            {{ k.label }}
          </option>
        </select>
        <select
          v-model="nextStep"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          @change="load"
        >
          <option value="">Anyone's move</option>
          <option value="team">Our move</option>
          <option value="reporter">Waiting on the reporter</option>
        </select>
        <button
          class="px-3 py-1.5 text-sm rounded-md border border-dark-primary text-dark-primary
                 hover:bg-dark-primary hover:text-white"
          @click="load"
        >
          Refresh
        </button>

        <label v-if="visibleRequests.length" class="flex items-center gap-1.5 text-sm ml-1">
          <input
            type="checkbox"
            class="h-4 w-4"
            :checked="allSelected"
            :indeterminate.prop="someSelected && !allSelected"
            @change="toggleAll"
          />
          Select all
        </label>
      </div>

      <!-- Only here when something is selected: an empty bar on every visit
           is a row of controls that do nothing, and this band is already
           carrying the title, the count and three filters. -->
      <div
        v-if="selectedIds.length"
        class="mt-3 pt-3 border-t border-gray-400 flex items-center gap-2 flex-wrap"
      >
        <span class="text-sm font-semibold">
          {{ selectedIds.length }} selected
        </span>

        <select
          v-model="bulkStatus"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          :disabled="bulkBusy"
        >
          <option value="">Status — leave as is</option>
          <option v-for="s in REQUEST_STATUSES" :key="s.value" :value="s.value">
            Set status: {{ s.label }}
          </option>
        </select>

        <select
          v-model="bulkKind"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm"
          :disabled="bulkBusy"
        >
          <option value="">Type — leave as is</option>
          <option v-for="k in REQUEST_KINDS" :key="k.value" :value="k.value">
            Set type: {{ k.label }}
          </option>
        </select>

        <!-- Whose move is not a field: it is read off the status (only
             'Need More Information' means the reporter's move). So this
             writes a status, and says which one it writes rather than
             pretending to be an axis of its own. Disabled while an explicit
             status is chosen, because the two would fight over one field. -->
        <select
          v-model="bulkMove"
          class="h-9 px-2 border border-gray-400 rounded-md text-sm
                 disabled:bg-gray-100 disabled:text-gray-400"
          :disabled="bulkBusy || Boolean(bulkStatus)"
          :title="bulkStatus
            ? 'Whose move follows from the status you picked above.'
            : 'Whose move it is, written as a status.'"
        >
          <option value="">Whose move — leave as is</option>
          <option value="reporter">Over to the reporter (Need More Information)</option>
          <option value="team">Back to us (Under Review)</option>
        </select>

        <button
          class="px-3 py-1.5 rounded-md text-sm text-white bg-accent-primary
                 hover:bg-accent-secondary disabled:opacity-50"
          :disabled="!bulkReady || bulkBusy"
          @click="applyBulk"
        >
          {{ bulkBusy
            ? `Applying ${bulkDone} of ${selectedIds.length}…`
            : `Apply to ${selectedIds.length}` }}
        </button>

        <button
          class="text-sm text-gray-600 underline"
          :disabled="bulkBusy"
          @click="clearSelection"
        >
          Clear selection
        </button>

        <span v-if="bulkNote" class="text-sm" :class="bulkFailed ? 'text-red-600' : 'text-green-700'">
          {{ bulkNote }}
        </span>
      </div>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <p v-if="!requests.length" class="mt-6 text-gray-500">No requests.</p>
      <p v-else-if="!visibleRequests.length" class="mt-6 text-gray-500">
        Nothing in this queue is waiting on us —
        <button class="underline" @click="needsUsOnly = false">show all {{ requests.length }}</button>.
      </p>

      <div
        v-for="item in visibleRequests"
        :key="item.id"
        class="border rounded-md bg-white p-3 mb-2"
        :class="needsUs(item)
          ? 'border-gray-300 border-l-4 border-l-orange-400'
          : 'border-gray-300'"
      >
        <div class="flex items-start justify-between gap-3">
          <!-- Its own click target: the card expands the thread, and a tick
               that also expanded it would make selecting a row impossible. -->
          <label class="shrink-0 pt-0.5 cursor-pointer" @click.stop>
            <input
              type="checkbox"
              class="h-4 w-4"
              :checked="isSelected(item.id)"
              @change="toggleOne(item.id)"
            />
          </label>
          <!-- The request itself is the disclosure control. Opening the
               editor moved to its own button: one click cannot mean both,
               and reading the thread is the commoner of the two. -->
          <div
            class="min-w-0 flex-1 cursor-pointer"
            :title="isExpanded(item.id) ? 'Click to collapse' : 'Click to read the thread'"
            @click="toggleExpanded(item.id)"
          >
            <div class="flex items-start gap-1.5">
              <chevron-right-icon
                class="h-4 w-4 text-gray-400 shrink-0 mt-0.5 transition-transform"
                :class="isExpanded(item.id) ? 'rotate-90' : ''"
              />
              <p class="whitespace-pre-line text-sm">{{ item.body }}</p>
            </div>
            <p class="text-xs text-gray-500 mt-1 ml-5">
              {{ item.reporter_display }} · {{ formatDateTimeWithDay(item.created_at) }}
              <span v-if="item.department_name"> · {{ item.department_name }}</span>
              <span v-if="item.work_item_title">
                · resolves via “{{ item.work_item_title }}”
              </span>
              <span v-else class="text-orange-700"> · no backlog item yet</span>
              <span v-if="item.posts && item.posts.length">
                · {{ item.posts.length }} post{{ item.posts.length === 1 ? '' : 's' }}
              </span>
            </p>
          </div>
          <div class="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            <!-- Loudest chip on the row, and first, because it is the one
                 that means somebody is sitting waiting on an answer. -->
            <span
              v-if="awaitingReply(item)"
              class="text-xs px-2 py-0.5 rounded bg-orange-200 text-orange-900 font-semibold"
              title="The reporter has posted something we have not answered"
            >Needs reply</span>
            <span
              v-if="item.kind"
              class="text-xs px-1.5 py-0.5 rounded"
              :class="requestKindChip(item.kind)"
            >{{ item.kind_display }}</span>
            <span
              class="text-xs px-2 py-0.5 rounded"
              :class="requestStatusChip(item.status)"
            >{{ item.status_display }}</span>
            <span
              v-if="item.estimate_display"
              class="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700"
            >{{ item.estimate_display }}</span>
            <span
              v-if="item.next_step_label"
              class="text-xs px-2 py-0.5 rounded"
              :class="nextStepChip(item.next_step_with)"
            >{{ item.next_step_label }}</span>
            <button
              class="text-xs underline text-gray-600 hover:text-dark-primary"
              title="Type it, estimate it, or give it a backlog item"
              @click.stop="editing = item"
            >Open</button>
          </div>
        </div>

        <!-- The thread, on My Requests' shape: the person who asked on the
             left in colour, us on the right. Indented under the request,
             because every post on it belongs to it. -->
        <div v-if="isExpanded(item.id)" class="mt-3 ml-7 pl-3 border-l-2 border-gray-200">
          <p v-if="!(item.posts && item.posts.length)" class="text-sm text-gray-500">
            Nothing posted yet — start the conversation below.
          </p>
          <div v-else class="space-y-2">
            <div
              v-for="post in item.posts"
              :key="post.id"
              class="flex"
              :class="fromReporter(item, post) ? 'justify-start' : 'justify-end'"
            >
              <div class="max-w-[80%]">
                <div class="rounded-2xl px-3 py-2" :class="bubbleClass(item, post)">
                  <p class="whitespace-pre-line text-sm">{{ post.body }}</p>
                </div>
                <p
                  class="text-xs text-gray-500 mt-1 flex items-center gap-1.5 flex-wrap"
                  :class="fromReporter(item, post) ? 'justify-start' : 'justify-end'"
                >
                  <span>{{ post.author_display || 'Back office team' }}</span>
                  <span>· {{ formatDateTimeWithDay(post.created_at) }}</span>
                  <!-- Spelled out on our own posts only. The reporter's own
                       words are shared by definition, and a chip saying so
                       on every one of them is noise. -->
                  <span
                    v-if="!fromReporter(item, post)"
                    class="px-1.5 py-0.5 rounded"
                    :class="post.visibility === 'shared'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-amber-100 text-amber-800'"
                  >{{ post.visibility === 'shared' ? 'reporter can see this' : 'internal' }}</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Two buttons rather than one button and a checkbox. Visibility
               is the one thing on this page that cannot be undone by
               editing it afterwards, so it is chosen per post and never
               carried over from the last one. -->
          <div class="mt-3" @click.stop>
            <textarea
              v-model="draft[item.id]"
              rows="2"
              class="w-full border border-gray-400 rounded-md px-2 py-1.5 text-sm"
              placeholder="Answer the reporter, or keep a note for us…"
              @keydown.meta.enter.prevent="sendPost(item, 'shared')"
              @keydown.ctrl.enter.prevent="sendPost(item, 'shared')"
            ></textarea>
            <div class="flex items-center gap-2 mt-1.5 flex-wrap">
              <button
                class="px-3 py-1.5 rounded-md text-sm text-white bg-accent-primary
                       hover:bg-accent-secondary disabled:opacity-50"
                :disabled="!canSend(item) || postingId === item.id"
                @click="sendPost(item, 'shared')"
              >
                {{ postingId === item.id && postingAs === 'shared'
                  ? 'Sending…' : 'Reply to reporter' }}
              </button>
              <button
                class="px-3 py-1.5 rounded-md text-sm border border-gray-400 text-gray-700
                       hover:bg-gray-100 disabled:opacity-50"
                :disabled="!canSend(item) || postingId === item.id"
                @click="sendPost(item, 'internal')"
              >
                {{ postingId === item.id && postingAs === 'internal'
                  ? 'Saving…' : 'Internal note' }}
              </button>
              <span class="text-xs text-gray-500">⌘ / Ctrl + Enter replies</span>
              <span v-if="postError[item.id]" class="text-sm text-red-600">
                {{ postError[item.id] }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <request-edit-modal
      :item="editing"
      :departments="departments"
      :work-items="workItems"
      @close="editing = null"
      @saved="onSaved"
      @items-added="onItemsAdded"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { formatDateTimeWithDay } from '@/composables/formatDateTime'

import { ChevronRightIcon } from '@heroicons/vue/20/solid'

import RequestEditModal from '@/components/back-office/RequestEditModal'
import {
  createPost,
  getDepartments,
  getRequests,
  getWorkItems,
  updateRequest,
} from '@/services/BackOffice'
import {
  REQUEST_CLOSED_STATUSES,
  REQUEST_KINDS,
  REQUEST_STATUSES,
  nextStepChip,
  requestKindChip,
  requestStatusChip,
} from '@/utils/requestOptions'

const store = useStore()
const token = () => store.getters['auth/showToken']

const requests = ref([])
const departments = ref([])
const workItems = ref([])
const total = ref(0)
const status = ref('')
const kind = ref('')
const nextStep = ref('')
const loading = ref(true)
const error = ref('')
const editing = ref(null)

// --- reading and answering a thread in place ------------------------------

const expandedIds = ref([])
// Drafts, errors and the in-flight row are keyed by request id so that
// expanding a second thread does not inherit the first one's half-typed
// reply, and a failure on one row says so on that row.
const draft = ref({})
const postError = ref({})
const postingId = ref(null)
const postingAs = ref('')
const needsUsOnly = ref(false)

function isExpanded(id) { return expandedIds.value.includes(id) }

function toggleExpanded(id) {
  expandedIds.value = isExpanded(id)
    ? expandedIds.value.filter(x => x !== id)
    : [...expandedIds.value, id]
}

function collapseAll() { expandedIds.value = [] }

// Which side of the thread a post belongs on. By author id, not by name:
// RequestSerializer gives triage both `post.author` and `request.reporter`,
// so there is no need to guess from a display string, and a stand-in post
// filed on the reporter's behalf still reads as theirs.
function fromReporter(item, post) {
  return post.author != null && post.author === item.reporter
}

function bubbleClass(item, post) {
  if (fromReporter(item, post)) {
    return 'bg-accent-primary text-white rounded-bl-sm'
  }
  // An internal note is not part of the conversation the reporter is having,
  // so it does not get to look like one: dashed and amber, the same colour
  // the chip underneath it uses.
  if (post.visibility !== 'shared') {
    return 'bg-amber-50 text-gray-900 border border-dashed border-amber-300 rounded-br-sm'
  }
  return 'bg-gray-100 text-gray-900 border border-gray-300 rounded-br-sm'
}

function isClosed(item) {
  return REQUEST_CLOSED_STATUSES.includes(item.status)
}

// The reporter has said something and we have not answered it.
//
// Only a SHARED post of ours counts as an answer -- an internal note is the
// team talking to itself, and the reporter is still sitting there waiting.
// Timestamps compare as strings because they all arrive as the same UTC ISO
// format from DRF.
function awaitingReply(item) {
  if (isClosed(item)) return false
  let askedAt = null
  let answeredAt = null
  for (const post of item.posts || []) {
    if (fromReporter(item, post)) askedAt = post.created_at
    else if (post.visibility === 'shared') answeredAt = post.created_at
  }
  if (!askedAt) return false
  return !answeredAt || answeredAt < askedAt
}

// Two different things, both ours to do: an unanswered question, or a status
// that says the next move is the team's. Closed requests are excluded from
// both -- a reporter's "thanks" on a shipped request would otherwise light
// up the queue forever.
function needsUs(item) {
  if (isClosed(item)) return false
  return awaitingReply(item) || item.next_step_with === 'team'
}

const needsUsCount = computed(() => requests.value.filter(needsUs).length)
const awaitingReplyCount = computed(() => requests.value.filter(awaitingReply).length)

// Counted over everything the server filters matched, filtered over the same
// set: the headline number must not change just because you clicked it.
const visibleRequests = computed(
  () => (needsUsOnly.value ? requests.value.filter(needsUs) : requests.value),
)

function canSend(item) {
  return Boolean((draft.value[item.id] || '').trim())
}

async function sendPost(item, visibility) {
  if (!canSend(item) || postingId.value === item.id) return
  postingId.value = item.id
  postingAs.value = visibility
  postError.value = { ...postError.value, [item.id]: '' }
  try {
    const created = await createPost(token(), {
      target_type: 'Request',
      target_id: item.id,
      body: draft.value[item.id].trim(),
      visibility,
    })
    // Appended to the row in place rather than reloading: a reload would
    // collapse nothing but would re-sort and re-fetch the whole queue to
    // show one line somebody just typed.
    if (!Array.isArray(item.posts)) item.posts = []
    item.posts.push(created)
    draft.value = { ...draft.value, [item.id]: '' }
  } catch (e) {
    postError.value = { ...postError.value, [item.id]: describe(e) }
  } finally {
    postingId.value = null
    postingAs.value = ''
  }
}

// --- selecting rows, and changing them together ---------------------------
//
// A plain array of ids rather than a Set: triage holds tens of rows, not
// thousands, so the linear lookup costs nothing and the value stays easy to
// read in devtools.
const selectedIds = ref([])
const bulkStatus = ref('')
const bulkKind = ref('')
const bulkMove = ref('')
const bulkBusy = ref(false)
const bulkDone = ref(0)
const bulkNote = ref('')
const bulkFailed = ref(false)

function isSelected(id) { return selectedIds.value.includes(id) }

function toggleOne(id) {
  selectedIds.value = isSelected(id)
    ? selectedIds.value.filter(x => x !== id)
    : [...selectedIds.value, id]
}

// Select-all means the rows actually on screen, which is the filtered set --
// ticking it while a filter is on must not quietly reach rows the filter is
// hiding. That now includes the needs-us filter, which the server knows
// nothing about.
const allSelected = computed(
  () => visibleRequests.value.length > 0
    && visibleRequests.value.every(r => selectedIds.value.includes(r.id)),
)
const someSelected = computed(() => selectedIds.value.length > 0)

function toggleAll() {
  selectedIds.value = allSelected.value ? [] : visibleRequests.value.map(r => r.id)
}

function clearSelection() {
  selectedIds.value = []
  bulkNote.value = ''
}

const bulkReady = computed(
  () => Boolean(bulkStatus.value || bulkKind.value || bulkMove.value),
)

// What this row would be changed to, or null for "nothing to do".
//
// Whose move is derived from status on the server, so asking for it here
// writes a status. 'Back to us' only touches rows that are actually waiting
// on the reporter: every other open row is already our move, and rewriting
// an In Progress row to Under Review would walk it backwards for no reason.
function bulkPayloadFor(row) {
  const payload = {}
  if (bulkKind.value) payload.kind = bulkKind.value
  if (bulkStatus.value) {
    payload.status = bulkStatus.value
  } else if (bulkMove.value === 'reporter') {
    payload.status = 'needs_info'
  } else if (bulkMove.value === 'team' && row.status === 'needs_info') {
    payload.status = 'under_review'
  }
  // Already as asked: sending it anyway would stamp updated_at and write a
  // history row saying nothing changed.
  for (const key of Object.keys(payload)) {
    if (row[key] === payload[key]) delete payload[key]
  }
  return Object.keys(payload).length ? payload : null
}

async function applyBulk() {
  bulkBusy.value = true
  bulkDone.value = 0
  bulkNote.value = ''
  bulkFailed.value = false

  let changed = 0
  let untouched = 0
  const failures = []

  // One at a time, through the same endpoint a single edit uses, so every
  // row gets the same validation, permissions and history it would get if
  // somebody opened it and saved it by hand.
  for (const id of [...selectedIds.value]) {
    const row = requests.value.find(r => r.id === id)
    if (!row) { untouched += 1; bulkDone.value += 1; continue }
    const payload = bulkPayloadFor(row)
    if (!payload) { untouched += 1; bulkDone.value += 1; continue }
    try {
      const updated = await updateRequest(token(), id, payload)
      Object.assign(row, updated)
      changed += 1
    } catch (e) {
      failures.push(`#${id} ${describe(e)}`)
    }
    bulkDone.value += 1
  }

  bulkFailed.value = failures.length > 0
  bulkNote.value = [
    `${changed} changed`,
    untouched ? `${untouched} already as asked` : '',
    failures.length ? `${failures.length} failed — ${failures[0]}` : '',
  ].filter(Boolean).join(' · ')

  bulkBusy.value = false
  bulkStatus.value = ''
  bulkKind.value = ''
  bulkMove.value = ''
  // Reload so rows the filters no longer match drop out, then drop any
  // selection pointing at a row that is no longer on screen.
  await load()
  const onScreen = new Set(visibleRequests.value.map(r => r.id))
  selectedIds.value = selectedIds.value.filter(id => onScreen.has(id))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const params = { get_all: 'true' }
    if (status.value) params.status = status.value
    if (kind.value) params.kind = kind.value
    if (nextStep.value) params.next_step_with = nextStep.value
    const data = await getRequests(token(), params)
    requests.value = Array.isArray(data) ? data : (data.results || [])
    total.value = requests.value.length
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

// The modal hands back the request as the server now has it, so the row in
// the list behind it follows rather than going stale.
// Mutated in place rather than replaced: `editing` IS the row, and handing
// the modal a new object identity would re-snapshot its form and throw away
// anything half-typed -- which is exactly what happens when somebody adds a
// post before saving a status change.
function onSaved(updated) {
  const row = requests.value.find(r => r.id === updated.id)
  if (row) Object.assign(row, updated)
  else if (editing.value && editing.value.id === updated.id) {
    Object.assign(editing.value, updated)
  }
}

// Work items made from inside the modal, so the parent picker offers them on
// the next request without a refetch. Matched on id because attaching an item
// hands back rows this list may already hold.
function onItemsAdded(items) {
  for (const item of items) {
    const existing = workItems.value.find(w => w.id === item.id)
    if (existing) Object.assign(existing, item)
    else workItems.value.push(item)
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(async () => {
  try {
    departments.value = await getDepartments(token())
  } catch (e) {
    // Non-fatal: the department picker just stays empty.
  }
  try {
    // Unfiltered, for the backlog picker in the modal and for naming an
    // item a request is already attached to.
    const data = await getWorkItems(token(), { get_all: 'true' })
    workItems.value = Array.isArray(data) ? data : (data.results || [])
  } catch (e) {
    // Non-fatal: the modal falls back to "create new".
  }
  await load()
})
</script>
